import type { UnitFilterRule } from '@r6digital/rapidstor-types/react/entities/Journey';
import type { SpaceCalculatorValues } from '@r6digital/rapidstor-types/types/Site';

/** `null` until the customer moves the slider, so an untouched form can be told apart from zeros. */
export type SliderValue = number | null;

export interface MovingHouseInput {
    bedrooms: SliderValue;
    smallRooms: SliderValue;
    largeRooms: SliderValue;
    garages: SliderValue;
}

export interface ExtraSpaceInput {
    rooms: SliderValue;
    largeItems: SliderValue;
    smallItems: SliderValue;
    boxes: SliderValue;
}

export interface BusinessStorageInput {
    boxes: SliderValue;
    smallItems: SliderValue;
    largeItems: SliderValue;
}

export interface CustomNeedsInput {
    width: SliderValue;
    length: SliderValue;
    smallItems: SliderValue;
    largeItems: SliderValue;
    boxes: SliderValue;
}

export type SpaceCalculatorMode =
    | 'none'
    | 'movingHouse'
    | 'extraSpace'
    | 'businessStorage'
    | 'customNeeds';

export type SpaceCalculatorResult =
    | { vehicle: true }
    | { vehicle: false; lower: number; upper: number };

export const EMPTY_MOVING_HOUSE: MovingHouseInput = {
    bedrooms: null, smallRooms: null, largeRooms: null, garages: null,
};

export const EMPTY_EXTRA_SPACE: ExtraSpaceInput = {
    rooms: null, largeItems: null, smallItems: null, boxes: null,
};

export const EMPTY_BUSINESS_STORAGE: BusinessStorageInput = {
    boxes: null, smallItems: null, largeItems: null,
};

export const EMPTY_CUSTOM_NEEDS: CustomNeedsInput = {
    width: null, length: null, smallItems: null, largeItems: null, boxes: null,
};

export const VEHICLE_STORAGE_RESULT: SpaceCalculatorResult = { vehicle: true };

const PARKING_NAME = 'parking';

/** Width added to a calculated area to form the band of unit sizes shown, in the site's area unit. */
const AREA_BAND_WIDTH = 2;

const allUntouched = (input: object): boolean => (
    Object.values(input).every((value) => value === null)
);

const valueOf = (value: SliderValue): number => value ?? 0;

/**
 * The band of unit areas to show for a calculated total. Below the lowest
 * threshold and at or above the highest, the site's configured bands apply.
 */
export const areaBandFor = (
    totalSize: number,
    values: SpaceCalculatorValues,
): SpaceCalculatorResult => {
    const { lowestValue, highestValue } = values;
    if (totalSize < lowestValue.area) {
        return { vehicle: false, lower: lowestValue.low, upper: lowestValue.high };
    }
    if (totalSize >= highestValue.area) {
        return { vehicle: false, lower: highestValue.low, upper: highestValue.high };
    }
    return { vehicle: false, lower: totalSize, upper: totalSize + AREA_BAND_WIDTH };
};

export const calculateMovingHouse = (
    input: MovingHouseInput,
    values: SpaceCalculatorValues,
): SpaceCalculatorResult | null => {
    if (allUntouched(input)) {
        return null;
    }
    const totalSize = valueOf(input.bedrooms) * values.bedroom
        + valueOf(input.largeRooms) * values.largeRoom
        + valueOf(input.smallRooms) * values.smallRoom
        + valueOf(input.garages) * values.garage;
    return areaBandFor(totalSize, values);
};

/** Items take precedence: any item count discards the room count. */
export const calculateExtraSpace = (
    input: ExtraSpaceInput,
    values: SpaceCalculatorValues,
): SpaceCalculatorResult | null => {
    if (allUntouched(input)) {
        return null;
    }
    const roomsTotal = valueOf(input.rooms) * values.room;
    const goodsTotal = valueOf(input.largeItems) * values.largeItem
        + valueOf(input.smallItems) * values.smallItem
        + valueOf(input.boxes) * values.box;
    return areaBandFor(goodsTotal > 0 ? goodsTotal : roomsTotal, values);
};

export const calculateBusinessStorage = (
    input: BusinessStorageInput,
    values: SpaceCalculatorValues,
): SpaceCalculatorResult | null => {
    if (allUntouched(input)) {
        return null;
    }
    const totalSize = valueOf(input.boxes) * values.box
        + valueOf(input.smallItems) * values.smallItem
        + valueOf(input.largeItems) * values.largeItem;
    return areaBandFor(totalSize, values);
};

/** Items take precedence: any item count discards the width × length. */
export const calculateCustomNeeds = (
    input: CustomNeedsInput,
    values: SpaceCalculatorValues,
): SpaceCalculatorResult | null => {
    if (allUntouched(input)) {
        return null;
    }
    const footprint = valueOf(input.width) * valueOf(input.length);
    const goodsTotal = valueOf(input.smallItems) * values.smallItem
        + valueOf(input.largeItems) * values.largeItem
        + valueOf(input.boxes) * values.box;
    return areaBandFor(goodsTotal > 0 ? goodsTotal : footprint, values);
};

export const isParkingTypeName = (typeName: string): boolean => (
    typeName.toLowerCase().includes(PARKING_NAME)
);

/**
 * The listing filter a result applies: parking only for vehicle storage,
 * otherwise the area band with parking excluded.
 */
export const spaceCalculatorFilterRules = (result: SpaceCalculatorResult): UnitFilterRule[] => {
    if (result.vehicle) {
        return [{ mode: 'include', unitTypeNames: [PARKING_NAME], unitTypeNameMatch: 'contains' }];
    }
    return [
        {
            mode: 'include',
            // The legacy listing treated a 0 bound as no bound.
            area: { min: result.lower || undefined, max: result.upper || undefined },
        },
        { mode: 'exclude', unitTypeNames: [PARKING_NAME], unitTypeNameMatch: 'contains' },
    ];
};
